import React, { useState, KeyboardEvent, useRef, useEffect } from 'react';
import { AiStarIcon, ButtonV2, Select } from '@medeloop/medeloop-components-library';
import { useTranslation } from 'react-i18next';
import { useGetUserOverview } from 'src/api/users-queries';
import { dataSourceSelected } from 'src/store/create-cohort';
import { useDispatch } from 'react-redux';

enum DataSourceToHide {
  HealthVerityPilot = '24400e33-36c3-433f-94a2-23a491421fbf',
}

interface ChatInputProps {
  onSendMessage?: (message: string) => void;
  disabled?: boolean;
}

export const ChatInput = ({ onSendMessage, disabled = false }: ChatInputProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [message, setMessage] = useState<string>('');
  const [hasSelected, setHasSelected] = useState<boolean>(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { data, isPending } = useGetUserOverview();
  const dataSetOptions =
    data && !isPending
      ? data?.dataSources
          .map((dataSet) => ({
            label: dataSet.dataSourceDisplayName,
            value: dataSet.dataSourceId,
          }))
          .filter((dataSource) => dataSource.value !== DataSourceToHide.HealthVerityPilot)
      : [{ label: 'No data sources available', value: 'No data' }];

  useEffect(() => {
    if (textareaRef.current) {
      // Reset height to auto to get the correct scrollHeight
      textareaRef.current.style.height = 'auto';
      // Set the height to the scrollHeight to fit all content
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [message]);
  const disabledButton = !(hasSelected && message.length > 0);

  const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only focus if the click wasn't on a button
    if (!(e.target as Element).closest('button')) {
      textareaRef.current?.focus();
    }
  };

  const handleSend = () => {
    const handleMessageForSend = () => {
      return message.trim().replace(/'/g, "\\'");
    };
    const finalizeMessage = handleMessageForSend();
    if (!finalizeMessage) return;
    onSendMessage && onSendMessage(finalizeMessage);
    setMessage('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!hasSelected) {
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    } else if (e.key === 'Escape') {
      setMessage('');
    }
  };

  const handleSelectChange = (value: string) => {
    const rootOrganizationId =
      data?.dataSources.find((dataSource) => dataSource.dataSourceId === value)
        ?.rootOrganizationId ?? null;
    dispatch(dataSourceSelected({ dataSourceId: value, rootOrganizationId }));
    setHasSelected(true);
  };

  return (
    <div className="w-full flex flex-col">
      <div
        role="textbox"
        tabIndex={0}
        className={`h-fit p-4 bg-background-dark-subdued rounded-xl border border-stroke-on-dark-subdued flex-col justify-start items-start gap-6 inline-flex w-full ${
          !disabled ? 'cursor-text' : 'cursor-default'
        }`}
        onClick={handleContainerClick}
      >
        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder="Type your cohort description here..."
          className="w-full bg-transparent text-content-on-dark-default text-sm font-normal focus:outline-none"
        />
        <div className="self-stretch justify-between items-start inline-flex">
          <div className="inline-flex">
            <Select
              className="w-52"
              options={dataSetOptions}
              placeholder="Select dataset"
              onValueChange={handleSelectChange}
              disabled={isPending || disabled}
            />
          </div>
          <ButtonV2 onClick={handleSend} disabled={disabledButton}>
            <span className="flex items-center gap-2">
              <AiStarIcon />
              {t('create_cohort')}
            </span>
          </ButtonV2>
        </div>
      </div>
    </div>
  );
};
